import {ReactElement, useEffect, useState} from "react";
import {Field, Input, Label, Message} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {Col, Row} from "@zendeskgarden/react-grid";
import {
    PayrollCompensationItemCalculationType,
    PayrollCompensationItemModel,
    PayrollCompensationItemType,
} from "../../../../../__generated__/generated_types";
import styles from "../PayrollDefaults.module.scss";
import {LocalCompItemDefault} from "../DefaultsForm";
import PayrollDatePicker from "../../../../../components/analyst-payroll-drawer/components/PayrollDatePicker";
import {ensureDateInUtc} from "../../../../../components/analyst-payroll-drawer/date-helpers";
import {v4 as uuid} from "uuid";

interface DefaultsBenefitsProps {
    compItemDefaults: LocalCompItemDefault[];
    setCompItemDefaults: (items: LocalCompItemDefault[]) => void;
    compensationItems: Pick<PayrollCompensationItemModel, "id" | "type" | "customName">[];
    onResetType: (type: PayrollCompensationItemType) => void;
    isTypeChanged: (type: PayrollCompensationItemType) => boolean;
    onValidationChange?: (hasErrors: boolean) => void;
    showResetControls?: boolean;
}

type ValidationErrors = Record<string, string>;
type TouchedFields = Record<string, boolean>;

const amountOnlyBenefitTypes = [
    PayrollCompensationItemType.Medical,
    PayrollCompensationItemType.Dental,
    PayrollCompensationItemType.Vision,
];

const percentOnlyBenefitTypes = [
    PayrollCompensationItemType.Retirement_401KMatch,
];

function hasValue(value: string | undefined | null): boolean {
    return (value ?? "").trim().length > 0;
}

export default function DefaultsBenefits(props: DefaultsBenefitsProps): ReactElement {
    const {
        compItemDefaults,
        setCompItemDefaults,
        compensationItems,
        onResetType,
        isTypeChanged,
        onValidationChange,
        showResetControls = true,
    } = props;
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [touchedFields, setTouchedFields] = useState<TouchedFields>({});

    function markTouched(fieldKey: string) {
        setTouchedFields(current => ({...current, [fieldKey]: true}));
    }

    function fieldError(fieldKey: string): string | undefined {
        return touchedFields[fieldKey] ? errors[fieldKey] : undefined;
    }

    const benefitGroups = [
        {
            label: "Medical",
            type: PayrollCompensationItemType.Medical,
            hasCap: false,
            amountOnly: true,
        },
        {
            label: "Dental",
            type: PayrollCompensationItemType.Dental,
            hasCap: false,
            amountOnly: true,
        },
        {
            label: "Vision",
            type: PayrollCompensationItemType.Vision,
            hasCap: false,
            amountOnly: true,
        },
        {
            label: "401K",
            type: PayrollCompensationItemType.Retirement_401KMatch,
            hasCap: true,
            amountOnly: false,
            percentOnly: true,
        }
    ];

    useEffect(() => {
        const hasInvalid401kCalculationType = compItemDefaults.some(item =>
            percentOnlyBenefitTypes.includes(item.compensationItemType) &&
            item.calculationType !== PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation
        );

        if(!hasInvalid401kCalculationType) {
            return;
        }

        setCompItemDefaults(compItemDefaults.map(item => {
            if(!percentOnlyBenefitTypes.includes(item.compensationItemType)) {
                return item;
            }

            return {
                ...item,
                calculationType: PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation,
            };
        }));
    }, [compItemDefaults, setCompItemDefaults]);

    useEffect(() => {
        const nextErrors: ValidationErrors = {};

        compItemDefaults.forEach(item => {
            const amountOnly = amountOnlyBenefitTypes.includes(item.compensationItemType);
            const percentOnly = percentOnlyBenefitTypes.includes(item.compensationItemType);
            const amountLabel = amountOnly
                ? "Amount"
                : percentOnly
                ? "% of Salary"
                : item.calculationType === PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation
                ? "% of Salary"
                : "Amount";
            if(!hasValue(item.costPerPayPeriod)) nextErrors[`${item._localId}.costPerPayPeriod`] = `${amountLabel} is required`;
            if(!item.effectiveStartDate) nextErrors[`${item._localId}.effectiveStartDate`] = "Effective Date is required";
        });

        setErrors(nextErrors);
        onValidationChange?.(Object.keys(nextErrors).length > 0);
    }, [compItemDefaults, onValidationChange]);

    function getItemsForType(type: PayrollCompensationItemType): LocalCompItemDefault[] {
        return compItemDefaults.filter(item => item.compensationItemType === type);
    }

    function updateItem(localId: string, updates: Partial<LocalCompItemDefault>) {
        setCompItemDefaults(compItemDefaults.map(item =>
            item._localId === localId ? {...item, ...updates} : item
        ));
    }

    function removeItem(localId: string) {
        setCompItemDefaults(compItemDefaults.filter(item => item._localId !== localId));
    }

    function addItem(type: PayrollCompensationItemType) {
        const compItem = compensationItems.find(ci => ci.type === type);
        if(!compItem) return;
        setCompItemDefaults([...compItemDefaults, {
            _localId: uuid(),
            compensationItemId: compItem.id,
            compensationItemType: type,
            calculationType: percentOnlyBenefitTypes.includes(type)
                ? PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation
                : PayrollCompensationItemCalculationType.FixedAmount,
            costPerPayPeriod: "",
            annualCostCap: "",
            effectiveStartDate: undefined,
        }]);
    }

    return (
        <div className={styles.subForm}>
            {benefitGroups.map(group => {
                const items = getItemsForType(group.type);
                return (
                    <div className={styles.section} key={group.label}>
                        <div>
                            <Row justifyContent="between">
                                <Col><h4 className={styles.sectionHeader}>{group.label}</h4></Col>
                                {showResetControls && <Col textAlign="end"><Button isBasic disabled={!isTypeChanged(group.type)} onClick={() => onResetType(group.type)}>Undo</Button></Col>}
                            </Row>
                            {items.map((item, index) => {
                                const isPercent = item.calculationType === PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation;
                                const amountLabel = group.amountOnly ? "Amount" : group.percentOnly || isPercent ? "% of Salary" : "Amount";
                                const amountPlaceholder = group.amountOnly || (!group.percentOnly && !isPercent) ? "$" : "%";
                                return (
                                    <div key={item._localId}>
                                        <Field className={styles.field}>
                                            <Row justifyContent="between">
                                                <Col>
                                                    <Label className={styles.subHeader}>Payment {index + 1}</Label>
                                                </Col>
                                                <Button isBasic isDanger onClick={() => removeItem(item._localId)}>Remove</Button>
                                            </Row>
                                            <Row justifyContent="between">
                                                <Col>
                                                    <Field className={styles.field}>
                                                        <Label className={styles.label}>{amountLabel}</Label>
                                                        <Input
                                                            placeholder={amountPlaceholder}
                                                            value={item.costPerPayPeriod}
                                                            type="number"
                                                            validation={fieldError(`${item._localId}.costPerPayPeriod`) ? "error" : undefined}
                                                            onBlur={() => markTouched(`${item._localId}.costPerPayPeriod`)}
                                                            onChange={(evt) => updateItem(item._localId, {costPerPayPeriod: evt.target.value})}
                                                        />
                                                        {fieldError(`${item._localId}.costPerPayPeriod`) && <Message validation="error">{fieldError(`${item._localId}.costPerPayPeriod`)}</Message>}
                                                    </Field>
                                                </Col>
                                                {group.hasCap && isPercent && (
                                                    <Col>
                                                        <Field className={styles.field}>
                                                            <Label className={styles.label}>Compensation Cap</Label>
                                                            <Input
                                                                placeholder="$ up to"
                                                                value={item.annualCostCap}
                                                                type="number"
                                                                onChange={(evt) => updateItem(item._localId, {annualCostCap: evt.target.value})}
                                                            />
                                                        </Field>
                                                    </Col>
                                                )}
                                            </Row>
                                            <Row justifyContent="between">
                                                <Col size={12}>
                                                    <Field className={styles.field}>
                                                        <Label className={styles.label}>Effective Date</Label>
                                                        <PayrollDatePicker
                                                            dateValue={item.effectiveStartDate ?? ""}
                                                            onDateChange={(newDate) => {
                                                                updateItem(item._localId, {effectiveStartDate: ensureDateInUtc(newDate)?.toISOString()});
                                                            }}
                                                            onBlur={() => markTouched(`${item._localId}.effectiveStartDate`)}
                                                        />
                                                        {fieldError(`${item._localId}.effectiveStartDate`) && <Message validation="error">{fieldError(`${item._localId}.effectiveStartDate`)}</Message>}
                                                    </Field>
                                                </Col>
                                            </Row>
                                        </Field>
                                    </div>
                                );
                            })}
                            <Button isBasic className={styles.addButton} onClick={() => addItem(group.type)}>Add</Button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
